import {
  computeCoverageSegments,
  COVERAGE_SEGMENT_KEYS,
  type CoverageSegmentKey,
  coverageSegmentLabel,
  FALLBACK_OUTCOME_COLOR,
  OUTCOME_COLOR,
} from '@src/lib/coverage';
import { type CoverageRow, formatCostUsd, scopeLabel, type StatsRow } from '@src/services/opsService';

export type ScopeStatus = 'good' | 'warning' | 'critical';

const STATUS_DOT_CLASS: Record<ScopeStatus, string> = {
  good: 'bg-status-good',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
};

const STATUS_TEXT_CLASS: Record<ScopeStatus, string> = {
  good: 'text-status-good',
  warning: 'text-status-warning',
  critical: 'text-status-critical',
};

/** Health tier for a scope's success rate, mirroring the validated prototype's thresholds. */
export function statusForRate(successRate: number): ScopeStatus {
  if (successRate >= 97) return 'good';
  if (successRate >= 90) return 'warning';
  return 'critical';
}

export function statusDotClass(status: ScopeStatus): string {
  return STATUS_DOT_CLASS[status];
}

export function statusTextClass(status: ScopeStatus): string {
  return STATUS_TEXT_CLASS[status];
}

export function formatUsd(amountUsd: number): string {
  return amountUsd > 0 ? formatCostUsd(amountUsd) : '—';
}

export function formatDurationMs(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

/** Coarse "N ago" relative time, matching the granularity the prototype's Overview tiles use. */
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'yesterday';
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export interface CoverageSegment {
  outcome: CoverageSegmentKey;
  label: string;
  count: number;
  pct: number;
  colorClass: string;
}

/**
 * Builds the tile's mini coverage-bar segments -- one per key in lib/coverage's
 * COVERAGE_SEGMENT_KEYS (the three record-store outcomes plus the real NEVER_RUN bucket),
 * delegating to computeCoverageSegments there so this bar and the dedicated Coverage screen
 * always derive counts from the exact same mapping.
 */
export function buildCoverageSegments(coverage: CoverageRow | undefined): CoverageSegment[] {
  const segments = coverage
    ? computeCoverageSegments(coverage)
    : COVERAGE_SEGMENT_KEYS.map((outcome) => ({ outcome, label: coverageSegmentLabel(outcome), count: 0, pct: 0 }));

  return segments.map((segment) => ({
    ...segment,
    colorClass: OUTCOME_COLOR[segment.outcome] ?? FALLBACK_OUTCOME_COLOR,
  }));
}

/** `attemptedProperties / totalIngested` -- the lifetime share of ingested properties that have a
 * successful record, i.e. `100 - ` the never-run segment's pct. `'—'` when there's nothing ingested
 * yet to compute a ratio from (attemptedProperties is then necessarily 0 too). Deliberately a
 * separate, lifetime figure from `rateLabel` (a windowed run-success rate, not a coverage ratio). */
export function formatCoveragePct(coverage: CoverageRow | undefined): string {
  if (!coverage || coverage.totalIngested === 0) return '—';
  return `${((coverage.attemptedProperties / coverage.totalIngested) * 100).toFixed(0)}%`;
}

export interface OverviewTileViewModel {
  scope: string;
  label: string;
  status: ScopeStatus;
  rateLabel: string;
  coveragePctLabel: string;
  runsLabel: string;
  costLabel: string;
  avgLatencyLabel: string;
  lastRunLabel: string;
  segments: CoverageSegment[];
  hasCoverageData: boolean;
}

/** Combines one scope's windowed stats with its lifetime coverage into the tile's view model. */
export function buildOverviewTile(stats: StatsRow, coverage: CoverageRow | undefined): OverviewTileViewModel {
  return {
    scope: stats.scope,
    label: scopeLabel(stats.scope),
    status: statusForRate(stats.successRate),
    rateLabel: `${stats.successRate.toFixed(1)}%`,
    coveragePctLabel: formatCoveragePct(coverage),
    runsLabel: stats.totalRuns.toLocaleString(),
    costLabel: formatUsd(stats.totalCostUsd),
    avgLatencyLabel: formatDurationMs(stats.p50DurationMs),
    lastRunLabel: formatRelativeTime(stats.lastRunAt),
    segments: buildCoverageSegments(coverage),
    hasCoverageData: coverage !== undefined,
  };
}

export interface TopStats {
  totalCostLabel: string;
  totalRunsLabel: string;
  overallRateLabel: string;
  attentionCount: number;
}

/**
 * Aggregates the per-scope stats rows into the top stat row. Mirrors the validated prototype:
 * "scopes needing attention" counts only the `critical` tier, not `warning`.
 */
export function buildTopStats(rows: StatsRow[]): TopStats {
  const totalCost = rows.reduce((sum, row) => sum + row.totalCostUsd, 0);
  const totalRuns = rows.reduce((sum, row) => sum + row.totalRuns, 0);
  const weightedRate =
    totalRuns > 0 ? rows.reduce((sum, row) => sum + row.successRate * row.totalRuns, 0) / totalRuns : 0;
  const attentionCount = rows.filter((row) => statusForRate(row.successRate) === 'critical').length;

  return {
    // Unlike the per-scope tile (formatUsd), the aggregate total shows a real "$0" rather than
    // an em dash for zero -- em dash means "this scope doesn't use AI", which doesn't apply to a
    // sum across scopes.
    totalCostLabel: formatCostUsd(totalCost),
    totalRunsLabel: totalRuns.toLocaleString(),
    overallRateLabel: `${weightedRate.toFixed(1)}%`,
    attentionCount,
  };
}
